/* eslint-disable no-console */
import { type ErrorInfo } from "react"

import {
    type ErrorEvent as SentryErrorEvent,
    type EventHint as SentryEventHint,
    Scope as SentryScope,
    User as SentryUser,
    addIntegration as addSentryIntegration,
    getCurrentScope as getCurrentSentryScope,
    init as initSentry,
    setUser as setSentryUser,
    withScope as withSentryScope
} from "@sentry/react"
import { Mutation, Query } from "@tanstack/react-query"
import { AxiosError, InternalAxiosRequestConfig, isAxiosError } from "axios"
import includes from "lodash-es/includes"
import isNumber from "lodash-es/isNumber"
import isString from "lodash-es/isString"

// dont shorten schedule import below, it breaks vite deps
import { ECoachingChatMutationKeyBase, ECoachingChatQueryKeyBase } from "@/3514/entities/coaching-chat/config"
import type { TCoachingChatSocketErrorEventResponse as TChatSocketErrorResponse } from "@/3514/entities/coaching-chat/types"
// dont shorten schedule imports below, it breaks vite deps
import {
    ESessionScheduleMutationKeyBase,
    ESessionScheduleQueryKeyBase
} from "@/3514/modals/session-schedule/session-schedule.modal.api.config"
// don't shorten auth import below, it breaks vite deps
import { EAuthMutationKeyBase, EAuthMutationUrl } from "@/3514/pages/auth/auth.api.config"
import { ECoachGroupQueryKeyBase } from "@/3514/pages/coach.group/coach.group.api.config"
// don't shorten auth import below, it breaks vite deps
import { EParticipantChapterMutationKeyBase } from "@/3514/pages/participant.chapter/config"
// dont shorten schedule imports below, it breaks vite deps
import {
    EParticipantOnboardingMutationKeyBase,
    EParticipantOnboardingQueryKeyBase
} from "@/3514/pages/participant.onboarding/participant.onboarding.api.config"
import type { IUserModel } from "@/3514/types"
import Urls from "@/api/urls"
import { JS_CONF } from "@/constants"
// dont shorten schedule imports below, it breaks vite deps
import { ESwapSessionMutationKeyBase } from "@/entities/swap-session/use-available-guest-sessions-mutation"

import {
    isBadGatewayError,
    isBadRequestError,
    isConflictError,
    isForbiddenError,
    isGatewayTimeoutError,
    isNetworkGeneralError,
    isNetworkTimeoutError,
    isNotFoundError,
    isPreconditionRequiredError,
    isServiceUnavailableError,
    isUnauthorizedError,
    isUnprocessableEntityError
} from "./axios.helpers"

function _validateQuery(err: AxiosError, query: Query): boolean {
    if (
        isUnauthorizedError(err) ||
        Boolean(isConflictError(err) && includes<unknown>(query.queryKey, ESessionScheduleQueryKeyBase.Widget)) ||
        Boolean(isBadRequestError(err) && includes<unknown>(query.queryKey, ESessionScheduleQueryKeyBase.Widget)) ||
        Boolean(isNotFoundError(err) && includes<unknown>(query.queryKey, ECoachGroupQueryKeyBase.Session)) ||
        Boolean(isBadRequestError(err) && includes<unknown>(query.queryKey, EParticipantOnboardingQueryKeyBase)) ||
        Boolean(
            isNotFoundError(err) && includes<unknown>(query.queryKey, ECoachingChatQueryKeyBase.CoachingChatNewMessages)
        )
    )
        return false

    return true
}

function _evaluateMutationInfoSeverity(err: AxiosError, mutation: Mutation): boolean {
    if (
        Boolean(
            Boolean(isBadRequestError(err) || isConflictError(err)) &&
                includes<unknown>(mutation.options.mutationKey, ECoachingChatMutationKeyBase.Conversation)
        )
    )
        return true

    return false
}

function _validateMutation(err: AxiosError, mutation: Mutation): boolean {
    if (
        isUnauthorizedError(err) ||
        Boolean(
            isConflictError(err) &&
                Boolean(
                    includes<unknown>(mutation.options.mutationKey, ESessionScheduleMutationKeyBase.Schedule) ||
                        includes<unknown>(mutation.options.mutationKey, ESessionScheduleMutationKeyBase.Reschedule)
                )
        ) ||
        Boolean(
            isBadRequestError(err) && includes<unknown>(mutation.options.mutationKey, EAuthMutationKeyBase.Login)
        ) ||
        Boolean(
            isBadRequestError(err) &&
                includes<unknown>(mutation.options.mutationKey, EParticipantChapterMutationKeyBase.CompleteChapter)
        ) ||
        Boolean(
            isBadRequestError(err) && includes<unknown>(mutation.options.mutationKey, ECoachingChatMutationKeyBase.Seed)
        ) ||
        Boolean(
            Boolean(isForbiddenError(err) || isBadRequestError(err)) &&
                includes<unknown>(mutation.options.mutationKey, EAuthMutationKeyBase.Pass)
        ) ||
        Boolean(isNotFoundError(err) && includes<unknown>(mutation.options.mutationKey, EAuthMutationKeyBase.SSO)) ||
        Boolean(isNotFoundError(err) && includes<unknown>(mutation.options.mutationKey, ESwapSessionMutationKeyBase)) ||
        Boolean(
            isBadRequestError(err) &&
                includes<unknown>(mutation.options.mutationKey, EParticipantOnboardingMutationKeyBase)
        ) ||
        Boolean(
            isUnprocessableEntityError(err) &&
                includes<unknown>(mutation.options.mutationKey, ECoachingChatMutationKeyBase.Seed)
        ) ||
        Boolean(
            isNotFoundError(err) &&
                includes<unknown>(mutation.options.mutationKey, ECoachingChatMutationKeyBase.Message)
        ) ||
        Boolean(
            isPreconditionRequiredError(err) &&
                includes<unknown>(mutation.options.mutationKey, ECoachingChatMutationKeyBase.Conversation)
        )
    )
        return

    return true
}

function passAxiosErrorToSentry(error: AxiosError, scope: SentryScope, options = { withInfoSeverity: false }): void {
    scope.setExtras({
        ...(!!error.config && {
            method: error.config.method,
            url: error.config.url,
            request_payload: error.config.data || null
        }),
        status_code: error?.response?.status
    })
    scope.setTag("build_config", process.env.NODE_ENV)
    scope.setTag("request_url", error.config.url)
    scope.setTag("method", error.config.method)

    if (
        options.withInfoSeverity ||
        isGatewayTimeoutError(error) ||
        isServiceUnavailableError(error) ||
        isBadGatewayError(error) ||
        isNetworkTimeoutError(error) ||
        isNetworkGeneralError(error) ||
        (isBadRequestError(error) && error.config.url === Urls.postTokenRefresh())
    )
        scope.setLevel("info")

    scope.captureException(error)
}

function passAxiosErrorFromQueryCacheToSentry(err: AxiosError, query: Query): void {
    if (_validateQuery(err, query))
        withSentryScope((scope: SentryScope): void => {
            scope.setContext("query", { queryHash: query.queryHash, data: err?.response?.data || null })
            scope.setFingerprint([query.queryHash.replaceAll(/[0-9]/g, "0")])

            passAxiosErrorToSentry(err, scope)
        })
}

function passAxiosErrorFromMutationCacheToSentry(err: AxiosError, mutation: Mutation): void {
    if (_validateMutation(err, mutation))
        withSentryScope((scope: SentryScope): void => {
            scope.setContext("mutation", {
                mutationId: mutation.mutationId,
                variables: mutation.state.variables,
                data: err.response?.data || null
            })

            if (mutation.options.mutationKey) scope.setFingerprint(Array.from(mutation.options.mutationKey) as string[])

            passAxiosErrorToSentry(err, scope, {
                withInfoSeverity: _evaluateMutationInfoSeverity(err, mutation)
            })
        })
}

function captureJwtAxiosErrorWithSentry(err: AxiosError): void {
    withSentryScope((scope: SentryScope): void => passAxiosErrorToSentry(err, scope))
}

function captureNetworkAxiosErrorWithSentry(err: AxiosError): void {
    withSentryScope((scope: SentryScope): void => {
        const { baseURL, headers, timeout }: InternalAxiosRequestConfig = err.config

        scope.setExtras({ baseURL, headers, timeout })

        scope.setLevel("info")

        passAxiosErrorToSentry(err, scope)
    })
}

function captureSentryRootError(error: Error | AxiosError, info: ErrorInfo): void {
    if (error instanceof AxiosError) return

    withSentryScope((scope: SentryScope): void => {
        scope.setExtras({
            errorInfo: isString(info) ? info : JSON.stringify(info)
        })
        scope.setTag("build_config", process.env.NODE_ENV)

        scope.captureException(error)
    })
}

function captureLazyLoadingErrorWithSentry(error: Error | unknown): void {
    withSentryScope((scope: SentryScope): void => {
        if (error instanceof Error)
            scope.setExtras({
                name: error.name,
                message: error.message,
                ...(isString(error.stack) && { stack: error.stack })
            })

        scope.setLevel("info")

        scope.captureException(error)
    })
}

function captureCoachingChatSocketExceptionWithSentry(
    resp: TChatSocketErrorResponse,
    conversationId: number | null
): void {
    withSentryScope((scope: SentryScope): void => {
        scope.setExtras({
            response_code: resp.code,
            ...("raw_response" in resp && { raw_response: resp.raw_response }),
            ...("moderation" in resp && { moderation: resp.moderation }),
            ...(isNumber(conversationId) && { conversation_id: conversationId })
        })

        scope.setLevel("warning")

        scope.captureMessage("CoachingChat Exception", "warning")
    })
}

function captureCoachingChatSocketErrorWithSentry(error: Error): void {
    withSentryScope((scope: SentryScope): void => {
        scope.setExtras({
            ...("name" in error && { name: error.name }),
            ...("message" in error && { message: error.message }),
            ...("stack" in error && { message: error.stack })
        })

        scope.setLevel("error")

        scope.captureException(error)
    })
}

function _initSentry(): void {
    const unauthorisedErrorsRegex: RegExp = new RegExp("Request failed with status code 401", "mi")
    const preloadErrorRegex: RegExp = new RegExp("Unable to preload", "mi")
    const modulePrefetcherErrorRegex: RegExp = new RegExp("Failed to fetch dynamically imported module", "mi")

    initSentry({
        dsn: JS_CONF.sentry_dsn_js,
        environment: JS_CONF.sentry_env,
        release: JS_CONF.version,
        attachStacktrace: true,
        normalizeDepth: 10,
        sampleRate: 1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1,
        ignoreErrors: [unauthorisedErrorsRegex, preloadErrorRegex, modulePrefetcherErrorRegex],
        beforeSend(event: SentryErrorEvent, hint: SentryEventHint): SentryErrorEvent | null {
            if (hint.mechanism?.type === "onunhandledrejection" && isAxiosError(hint.originalException)) return null

            if (isAxiosError(hint.originalException) && isConflictError(hint.originalException)) return null

            if (
                isAxiosError(hint.originalException) &&
                isNotFoundError(hint.originalException) &&
                hint.originalException.config?.url === EAuthMutationUrl.SSO()
            )
                return null

            return event
        }
    })

    console.log("\x1b[36m%s\x1b[0m", "Sentry session started")
}

async function loadSentryIntegrations(): Promise<void> {
    return import("@sentry/react").then(lazyLoadedSentry => {
        addSentryIntegration(lazyLoadedSentry.replayIntegration({ maskAllText: false, maskAllInputs: false }))
        addSentryIntegration(lazyLoadedSentry.browserTracingIntegration())
        addSentryIntegration(lazyLoadedSentry.extraErrorDataIntegration())
        addSentryIntegration(lazyLoadedSentry.captureConsoleIntegration({ levels: ["error"] }))
    })
}

function _setSentryUser(user: IUserModel, extra?: object): void {
    const userData: SentryUser = !!user
        ? {
              id: String(user.id),
              userId: String(user.userId),
              name: `${user.firstName} ${user.lastName}`,
              email: user.email,
              ...extra
          }
        : null

    setSentryUser(userData)

    console.log("\x1b[36m%s\x1b[0m", !!user ? "Sentry session user set" : "Sentry session user reset")
}

function resetSentrySession(): void {
    _setSentryUser(null)
    getCurrentSentryScope()?.clearBreadcrumbs()
}

export {
    passAxiosErrorFromQueryCacheToSentry,
    passAxiosErrorFromMutationCacheToSentry,
    captureSentryRootError,
    captureJwtAxiosErrorWithSentry,
    captureNetworkAxiosErrorWithSentry,
    captureLazyLoadingErrorWithSentry,
    captureCoachingChatSocketExceptionWithSentry,
    captureCoachingChatSocketErrorWithSentry,
    loadSentryIntegrations,
    _setSentryUser as setSentryUser,
    _initSentry as initSentry,
    resetSentrySession
}
